// hooks/useDocumentGeneration.js
import { useState, useEffect } from 'react';
import apiClient from '../../../apiClient';
import streamClient from '../../../streamClient';
import { extractSectionContent } from '../utils/sectionHelpers';

/**
 * Custom hook för dokumentgenerering och hantering
 * Hanterar streaming, state och autosave för dokument
 */
export const useDocumentGeneration = (sessionId) => {
  const [openDocs, setOpenDocs] = useState([]); // {docId,title,sections,streaming,template_id}
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [generatingDocs, setGeneratingDocs] = useState([]);

  // Hämta sparade dokument och tillgängliga mallar
  useEffect(() => {
    if (!sessionId) return;
    
    // Hämta sparade dokument
    apiClient
      .get(`/recorder/sessions/${sessionId}`)
      .then((res) => {
        const docs = res.data.generated_documents || [];
        if (docs.length === 0) return;

        const mapped = docs.map((d) => ({
          docId: Date.now() + Math.random(),   // unikt id
          template_id: d.template_id,
          title: d.title,
          sections: d.sections,
          streaming: false,
        }));
        setOpenDocs(mapped);
      })
      .catch((e) => console.error("Kunde inte hämta dokument:", e));

    // Hämta tillgängliga dokumentmallar
    apiClient
      .get('/api/documentmallar')
      .then((res) => {
        setAvailableTemplates(res.data || []);
      })
      .catch((e) => console.error("Kunde inte hämta dokumentmallar:", e));
  }, [sessionId]);

  // Autosave dokument
  useEffect(() => {
    if (!sessionId) return;

    // spara enbart dokument som inte längre streamar
    const finished = openDocs.filter((d) => !d.streaming);
    if (finished.length === 0) return;

    const payload = finished.map((d) => ({
      template_id: d.template_id,
      title: d.title,
      sections: d.sections.map((s) => ({
        title: s.title,
        content: s.content.trim() || "‎",
      })),
    }));

    const timer = setTimeout(() => {
      apiClient.patch(`/recorder/sessions/${sessionId}/update_documents`, {
        generated_documents: payload,
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [openDocs, sessionId]);

  // Generera nytt dokument med streaming
  const handleGenerateDocument = async (template, journalText, setActiveSubTab) => {
    const docId = `tmpl-${template.template_id}`;

    // Lägg till i generating docs
    setGeneratingDocs((docs) => [...docs, docId]);

    // Ta bort eventuellt existerande dokument med samma template_id
    setOpenDocs((d) => [
      ...d.filter((doc) => doc.template_id !== template.template_id),
      {
        docId,
        template_id: template.template_id,
        title: template.template_name,
        sections: template.sections.map((s) => ({
          title: s.section_title,
          content: "",
          order: s.order,
        })),
        streaming: true,
      },
    ]);

    if (setActiveSubTab) {
      setActiveSubTab(`doc-${docId}`);
    }

    let accum = "";
    try {
      await streamClient.post(
        "/api/document/generate",
        { template_id: template.template_id, journal_text: journalText },
        (chunk) => {
          accum += chunk;

          // Uppdatera varje chunk
          setOpenDocs((docs) =>
            docs.map((d) =>
              d.docId === docId
                ? {
                    ...d,
                    sections: d.sections.map((s) => ({
                      ...s,
                      content: extractSectionContent(accum, s.title),
                    })),
                  }
                : d
            )
          );
        }
      );

      // Streamen klar – flagga streaming=false
      setOpenDocs((docs) =>
        docs.map((d) =>
          d.docId === docId ? { ...d, streaming: false } : d
        )
      );
      
      // Ta bort från generating docs
      setGeneratingDocs((docs) => docs.filter((d) => d !== docId));
    } catch (e) {
      console.error("Dokument-generering:", e);
      // Ta bort från generating docs även vid fel
      setGeneratingDocs((docs) => docs.filter((d) => d !== docId));
    }
  };

  // Uppdatera dokument-sektion
  const handleDocSectionChange = (docId, idx, field, value) => {
    setOpenDocs((docs) =>
      docs.map((d) =>
        d.docId === docId
          ? {
              ...d,
              sections: d.sections.map((s, i) =>
                i === idx ? { ...s, [field]: value } : s
              ),
            }
          : d
      )
    );
  };

  // Stäng dokument
  const closeDocument = (docId) => {
    setOpenDocs((docs) => docs.filter((d) => d.docId !== docId));
    setGeneratingDocs((docs) => docs.filter((d) => d !== docId));
  };

  return {
    openDocs,
    setOpenDocs,
    generateDocument: handleGenerateDocument,
    closeDocument,
    availableTemplates,
    generatingDocs,
    handleDocSectionChange
  };
};